import { useState, useRef, useEffect, useCallback } from 'react';
import type Konva from 'konva';
import type { MaskPath, MaskTool, MaskOperation } from '../../types/gradientFade';
import { DEFAULT_VECTOR_MASK, DEFAULT_GRADIENT_FADE, updateGradientFromHandles } from '../../masks';

type KonvaMouseEvent = Konva.KonvaEventObject<MouseEvent>;

export interface UseMaskDrawingParams {
  maskEditMode: {
    elementId: string;
    tool: MaskTool;
    operation: MaskOperation;
  } | null;
  isMaskEditing: boolean;
  maskEditElement: import('../../types').Element | null | undefined;
  scale: number;
  onMaskEditChange?: (elementId: string, settings: any) => void;
  onExitMaskEdit?: () => void;
}

export interface UseMaskDrawingReturn {
  isDrawingMask: boolean;
  activeMaskPath: MaskPath | null;
  hasDraggedMask: boolean;
  handleMaskMouseDown: (e: KonvaMouseEvent) => void;
  handleMaskMouseMove: (e: KonvaMouseEvent) => void;
  handleMaskMouseUp: () => void;
  handleMaskDblClick: () => void;
  handleMaskPointDrag: (pathId: string, pointIndex: number, x: number, y: number) => void;
  handleMaskHandleDrag: (pathId: string, pointIndex: number, handleType: 'cpIn' | 'cpOut', x: number, y: number) => void;
  handleGradientHandleDrag: (handleType: 'start' | 'end' | 'center' | 'radius', x: number, y: number) => void;
  stageToElementCoords: (pointerX: number, pointerY: number, element: import('../../types').Element, stageScale: number) => { x: number; y: number };
}

export function useMaskDrawing({
  maskEditMode,
  isMaskEditing,
  maskEditElement,
  scale,
  onMaskEditChange,
  onExitMaskEdit,
}: UseMaskDrawingParams): UseMaskDrawingReturn {
  const [isDrawingMask, setIsDrawingMask] = useState(false);
  const [activeMaskPath, setActiveMaskPath] = useState<MaskPath | null>(null);
  const [hasDraggedMask, setHasDraggedMask] = useState(false);
  const lastClickTimeRef = useRef<number>(0);

  // Exit mask edit on Escape key
  useEffect(() => {
    if (!isMaskEditing) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExitMaskEdit?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMaskEditing, onExitMaskEdit]);

  // Clear active path when tool changes or mask edit mode ends
  useEffect(() => {
    setActiveMaskPath(null);
    setIsDrawingMask(false);
    setHasDraggedMask(false);
  }, [maskEditMode?.tool, maskEditMode?.elementId]);

  const stageToElementCoords = useCallback((pointerX: number, pointerY: number, element: import('../../types').Element, stageScale: number) => {
    const canvasX = pointerX / stageScale;
    const canvasY = pointerY / stageScale;
    const cx = element.x + element.width / 2;
    const cy = element.y + element.height / 2;
    const rad = -((element.rotation || 0) * Math.PI) / 180;
    const dx = canvasX - cx;
    const dy = canvasY - cy;
    const rx = dx * Math.cos(rad) - dy * Math.sin(rad);
    const ry = dx * Math.sin(rad) + dy * Math.cos(rad);
    return {
      x: rx + element.width / 2,
      y: ry + element.height / 2,
    };
  }, []);

  const handleMaskMouseDown = useCallback((e: KonvaMouseEvent) => {
    if (!isMaskEditing || !maskEditElement || !maskEditMode) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const localPos = stageToElementCoords(pos.x, pos.y, maskEditElement, scale);

    if (localPos.x < 0 || localPos.x > maskEditElement.width ||
        localPos.y < 0 || localPos.y > maskEditElement.height) {
      return;
    }

    if (maskEditMode.tool === 'lasso') {
      if (activeMaskPath && activeMaskPath.points.length >= 3) {
        const closedPath = { ...activeMaskPath, closed: true };
        const currentMask = (maskEditElement as any).opacitySettings?.mask || DEFAULT_VECTOR_MASK;
        const newPaths = [...currentMask.paths, closedPath];

        onMaskEditChange?.(maskEditElement.id, {
          opacitySettings: {
            ...((maskEditElement as any).opacitySettings || { mode: 'gradient-fade', uniform: 1 }),
            mask: { ...currentMask, enabled: true, paths: newPaths },
          },
        });
        setActiveMaskPath(null);
        setIsDrawingMask(false);
        setHasDraggedMask(false);
        return;
      }

      const newPath: MaskPath = {
        id: `path-${Date.now()}`,
        points: [{ x: localPos.x, y: localPos.y }],
        closed: false,
        operation: maskEditMode.operation,
      };
      setActiveMaskPath(newPath);
      setIsDrawingMask(true);
      setHasDraggedMask(false);
    } else if (maskEditMode.tool === 'pen') {
      const now = Date.now();
      const timeSinceLastClick = now - lastClickTimeRef.current;
      lastClickTimeRef.current = now;

      if (timeSinceLastClick < 300 && activeMaskPath && activeMaskPath.points.length >= 3) {
        const closedPath = { ...activeMaskPath, closed: true };
        const currentMask = (maskEditElement as any).opacitySettings?.mask || DEFAULT_VECTOR_MASK;
        const newPaths = [...currentMask.paths, closedPath];

        onMaskEditChange?.(maskEditElement.id, {
          opacitySettings: {
            ...((maskEditElement as any).opacitySettings || { mode: 'gradient-fade', uniform: 1 }),
            mask: { ...currentMask, enabled: true, paths: newPaths },
          },
        });
        setActiveMaskPath(null);
        setIsDrawingMask(false);
        return;
      }

      if (!activeMaskPath) {
        const newPath: MaskPath = {
          id: `path-${Date.now()}`,
          points: [{ x: localPos.x, y: localPos.y }],
          closed: false,
          operation: maskEditMode.operation,
        };
        setActiveMaskPath(newPath);
      } else {
        setActiveMaskPath({
          ...activeMaskPath,
          points: [...activeMaskPath.points, { x: localPos.x, y: localPos.y }],
        });
      }
    }
  }, [isMaskEditing, maskEditElement, maskEditMode, stageToElementCoords, scale, activeMaskPath, onMaskEditChange]);

  const handleMaskMouseMove = useCallback((e: KonvaMouseEvent) => {
    if (!isMaskEditing || !maskEditElement || !isDrawingMask || maskEditMode?.tool !== 'lasso') return;
    if (!activeMaskPath) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const localPos = stageToElementCoords(pos.x, pos.y, maskEditElement, scale);

    const points = activeMaskPath.points;
    const lastPt = points[points.length - 1];
    const dist = Math.sqrt((localPos.x - lastPt.x) ** 2 + (localPos.y - lastPt.y) ** 2);
    if (dist > 5) {
      setHasDraggedMask(true);
      setActiveMaskPath({
        ...activeMaskPath,
        points: [...activeMaskPath.points, { x: localPos.x, y: localPos.y }],
      });
    }
  }, [isMaskEditing, maskEditElement, isDrawingMask, maskEditMode, stageToElementCoords, scale, activeMaskPath]);

  const handleMaskMouseUp = useCallback(() => {
    if (!isMaskEditing || !maskEditElement || !maskEditMode) return;

    if (maskEditMode.tool === 'lasso' && activeMaskPath && hasDraggedMask) {
      if (activeMaskPath.points.length >= 3) {
        const closedPath = { ...activeMaskPath, closed: true };
        const currentMask = (maskEditElement as any).opacitySettings?.mask || DEFAULT_VECTOR_MASK;
        const newPaths = [...currentMask.paths, closedPath];

        onMaskEditChange?.(maskEditElement.id, {
          opacitySettings: {
            ...((maskEditElement as any).opacitySettings || { mode: 'gradient-fade', uniform: 1 }),
            mask: { ...currentMask, enabled: true, paths: newPaths },
          },
        });
      }
      setActiveMaskPath(null);
      setIsDrawingMask(false);
      setHasDraggedMask(false);
    }
  }, [isMaskEditing, maskEditElement, maskEditMode, onMaskEditChange, activeMaskPath, hasDraggedMask]);

  const handleMaskDblClick = useCallback(() => {
    if (!isMaskEditing || !maskEditElement || !maskEditMode) return;

    if (activeMaskPath && activeMaskPath.points.length >= 3) {
      const closedPath = { ...activeMaskPath, closed: true };
      const currentMask = (maskEditElement as any).opacitySettings?.mask || DEFAULT_VECTOR_MASK;
      const newPaths = [...currentMask.paths, closedPath];

      onMaskEditChange?.(maskEditElement.id, {
        opacitySettings: {
          ...((maskEditElement as any).opacitySettings || { mode: 'gradient-fade', uniform: 1 }),
          mask: { ...currentMask, enabled: true, paths: newPaths },
        },
      });
    }
    setActiveMaskPath(null);
    setIsDrawingMask(false);
    setHasDraggedMask(false);
  }, [isMaskEditing, maskEditElement, maskEditMode, onMaskEditChange, activeMaskPath]);

  const handleGradientHandleDrag = useCallback((
    handleType: 'start' | 'end' | 'center' | 'radius',
    x: number,
    y: number
  ) => {
    if (!isMaskEditing || !maskEditElement || !maskEditMode) return;

    const currentOpSettings = (maskEditElement as any).opacitySettings;
    const currentGradient = currentOpSettings?.gradient || DEFAULT_GRADIENT_FADE;

    const gradientPatch = updateGradientFromHandles(
      currentGradient,
      handleType,
      x,
      y,
      maskEditElement.width,
      maskEditElement.height
    );

    onMaskEditChange?.(maskEditElement.id, {
      opacitySettings: {
        ...currentOpSettings,
        gradient: { ...currentGradient, ...gradientPatch },
      },
    });
  }, [isMaskEditing, maskEditElement, maskEditMode, onMaskEditChange]);

  const handleMaskPointDrag = useCallback((pathId: string, pointIndex: number, x: number, y: number) => {
    if (!isMaskEditing || !maskEditElement) return;

    const currentOpSettings = (maskEditElement as any).opacitySettings;
    const currentMask = currentOpSettings?.mask || DEFAULT_VECTOR_MASK;

    const pathIndex = currentMask.paths.findIndex((p: any) => p.id === pathId);
    if (pathIndex === -1) return;

    const newPaths = [...currentMask.paths];
    const path = { ...newPaths[pathIndex] };
    const points = [...path.points];
    points[pointIndex] = { ...points[pointIndex], x, y };
    path.points = points;
    newPaths[pathIndex] = path;

    onMaskEditChange?.(maskEditElement.id, {
      opacitySettings: {
        ...currentOpSettings,
        mask: { ...currentMask, paths: newPaths },
      },
    });
  }, [isMaskEditing, maskEditElement, onMaskEditChange]);

  const handleMaskHandleDrag = useCallback((pathId: string, pointIndex: number, handleType: 'cpIn' | 'cpOut', x: number, y: number) => {
    if (!isMaskEditing || !maskEditElement) return;

    const currentOpSettings = (maskEditElement as any).opacitySettings;
    const currentMask = currentOpSettings?.mask || DEFAULT_VECTOR_MASK;

    const pathIndex = currentMask.paths.findIndex((p: any) => p.id === pathId);
    if (pathIndex === -1) return;

    const newPaths = [...currentMask.paths];
    const path = { ...newPaths[pathIndex] };
    const points = [...path.points];
    points[pointIndex] = { ...points[pointIndex], [handleType]: { x, y } };
    path.points = points;
    newPaths[pathIndex] = path;

    onMaskEditChange?.(maskEditElement.id, {
      opacitySettings: {
        ...currentOpSettings,
        mask: { ...currentMask, paths: newPaths },
      },
    });
  }, [isMaskEditing, maskEditElement, onMaskEditChange]);

  return {
    isDrawingMask,
    activeMaskPath,
    hasDraggedMask,
    handleMaskMouseDown,
    handleMaskMouseMove,
    handleMaskMouseUp,
    handleMaskDblClick,
    handleMaskPointDrag,
    handleMaskHandleDrag,
    handleGradientHandleDrag,
    stageToElementCoords,
  };
}
